import React from 'react';
import { useAuth } from '../context/AuthContext';
import { Building2, ChevronDown, Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

/**
 * OrgSwitcher — Sélecteur d'organisation
 * 
 * Visible uniquement si l'utilisateur possède ≥ 2 memberships actives.
 * Utilise activeOrg et setActiveOrg depuis AuthContext.
 * 
 * Sécurité:
 * - setActiveOrg valide côté JS que l'orgId est dans les memberships actives
 * - Le backend reste la source d'autorité (filtrage par user.partner_id / authorize)
 * - localStorage ne sert que de préférence visuelle, jamais d'autorité
 * 
 * Limitation Phase 4.3:
 * Les pages partenaire utilisent encore user.partner_id pour scoper les API.
 * Le changement d'org via ce composant ne change pas les données affichées
 * tant que Phase 4.5 n'a pas migré les pages vers activeOrg.org_id.
 */
const OrgSwitcher = () => {
  const { memberships, activeOrg, setActiveOrg } = useAuth();

  // Ne montrer que les memberships actives
  const activeMemberships = memberships.filter(m => m.status === 'active');

  // Invisible si < 2 orgs actives
  if (activeMemberships.length < 2) return null;

  const currentLabel = activeOrg?.org_nom || 'Organisation';
  const currentType = activeOrg?.org_type === 'partner' ? 'Partenaire' : 'Magasin';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors hover:bg-black/[0.04] border"
          style={{ borderColor: 'var(--ludo-border)', color: 'var(--ludo-text)' }}
          data-testid="org-switcher-trigger"
        >
          <Building2 className="h-4 w-4 text-primary flex-shrink-0" />
          <span className="truncate max-w-[180px]">{currentLabel}</span>
          <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72" data-testid="org-switcher-menu">
        <DropdownMenuLabel className="text-xs uppercase tracking-wider text-gray-500 font-heading">
          Mes organisations
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {activeMemberships.map((mb) => {
          const isSelected = activeOrg?.org_id === mb.org_id;
          const typeLabel = mb.org_type === 'partner' ? 'Partenaire' : 'Magasin';
          const capLabels = (mb.org_capabilities || []).map(c => 
            c === 'distributor' ? 'Distributeur' : c === 'publisher' ? 'Éditeur' : c === 'retailer' ? 'Magasin' : c
          ).join(', ');

          return (
            <DropdownMenuItem
              key={mb.org_id}
              onClick={() => setActiveOrg(mb.org_id)}
              className="flex items-start gap-3 py-2.5 cursor-pointer"
              data-testid={`org-switcher-item-${mb.org_id}`}
            >
              <div className={`mt-0.5 h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                isSelected ? 'bg-primary text-white' : 'bg-gray-100 text-gray-500'
              }`}>
                <Building2 className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${isSelected ? 'text-primary' : ''}`}>
                  {mb.org_nom}
                </p>
                <p className="text-xs text-gray-500">
                  {typeLabel}{capLabels ? ` · ${capLabels}` : ''} · {mb.role_in_org}
                </p>
              </div>
              {isSelected && (
                <Check className="h-4 w-4 text-primary flex-shrink-0 mt-1" />
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default OrgSwitcher;
